import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { EditorModule } from 'primeng/editor';
import { MessageModule } from 'primeng/message';

import { MailService } from '../../services/mail.service';
import { EmailDetail, DomainConfig, ComposeMail } from '../../models/mail.model';

@Component({
  selector: 'app-mail-compose',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
    EditorModule,
    MessageModule,
  ],
  templateUrl: './mail-compose.component.html',
  styleUrl: './mail-compose.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MailComposeComponent {
  @Input() visible = false;
  @Input() mode: 'new' | 'reply' = 'new';
  @Input() senderAddress = '';
  @Input() replyToEmail: EmailDetail | null = null;
  @Input() domainConfig: DomainConfig | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() sent = new EventEmitter<void>();

  recipient = '';
  subject = '';
  bodyHtml = '';
  sending = false;
  errorMessage = '';
  attachedFiles: File[] = [];

  constructor(
    private mailService: MailService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.mode === 'reply' && this.replyToEmail) {
      this.recipient = this.replyToEmail.sender;
      this.subject = this.replyToEmail.subject?.startsWith('Re:')
        ? this.replyToEmail.subject
        : `Re: ${this.replyToEmail.subject || ''}`;

      const originalDate = new Date(this.replyToEmail.date_received).toLocaleString('de-DE');
      const originalFrom = this.replyToEmail.sender_name
        ? `${this.replyToEmail.sender_name} &lt;${this.replyToEmail.sender}&gt;`
        : this.replyToEmail.sender;

      this.bodyHtml =
        `<br><br>` +
        `<div style="border-left: 2px solid #ccc; padding-left: 10px; color: #666;">` +
        `<p>Am ${originalDate} schrieb ${originalFrom}:</p>` +
        `${this.replyToEmail.body_html || this.replyToEmail.body_text || ''}` +
        `</div>`;
    }
  }

  get dialogTitle(): string {
    return this.mode === 'reply' ? 'Antworten' : 'Neue Email';
  }

  get allowedDomainsText(): string {
    return this.domainConfig?.allowed_sender_domains.join(', ') || '';
  }

  onSend(): void {
    if (!this.recipient || !this.subject) {
      this.errorMessage = 'Empfaenger und Betreff sind erforderlich';
      this.cdr.markForCheck();
      return;
    }

    // Domain-Validierung
    if (this.domainConfig) {
      const recipientDomain = this.recipient.split('@')[1]?.toLowerCase();
      if (!this.domainConfig.allowed_sender_domains.includes(recipientDomain)) {
        this.errorMessage = `Nur Emails an folgende Domains erlaubt: ${this.allowedDomainsText}`;
        this.cdr.markForCheck();
        return;
      }
    }

    this.errorMessage = '';
    this.sending = true;
    this.cdr.markForCheck();

    const mail: ComposeMail = {
      sender: this.senderAddress,
      recipient: this.recipient,
      subject: this.subject,
      body_html: this.bodyHtml,
      reply_to_message_id:
        this.mode === 'reply' && this.replyToEmail
          ? this.replyToEmail.message_id
          : undefined,
    };

    this.mailService.sendMail(mail, this.attachedFiles).subscribe({
      next: () => {
        this.sending = false;
        this.sent.emit();
      },
      error: (err) => {
        this.sending = false;
        this.errorMessage = err.error?.detail || 'Fehler beim Senden';
        this.cdr.markForCheck();
      },
    });
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.attachedFiles.push(...Array.from(input.files));
      this.cdr.markForCheck();
    }
    input.value = '';
  }

  removeFile(index: number): void {
    this.attachedFiles.splice(index, 1);
    this.cdr.markForCheck();
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  onClose(): void {
    this.closed.emit();
  }
}
